import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  HOW_TO_PLAY_GUIDES, 
  getHowToPlayGuide, 
  getAllHowToPlayGuides,
  HowToPlayGuide 
} from '../../constants/gameRules';
import { CARD_GAMES, getGameSlug } from '../../constants/games';
import { GameSelector } from '../../components/GameSelector';

interface HowToPlayPageProps {
  activeGame?: number | 'All';
  setActiveGame?: (gameId: number | 'All') => void;
  dbGames?: any[];
}

export const HowToPlayPage: React.FC<HowToPlayPageProps> = ({ activeGame, setActiveGame, dbGames }) => {
  const { gameSlug } = useParams<{ gameSlug?: string }>();
  const navigate = useNavigate();

  // Determine active guide:
  // 1. If activeGame is 'All', user wants to select a game, so do not resolve a guide (show GameSelector)
  // 2. From URL param :gameSlug
  // 3. Or fallback to activeGame if already selected (not 'All')
  // 4. Or null (showing GameSelector)
  const activeGuide: HowToPlayGuide | null = useMemo(() => {
    if (activeGame === 'All') {
      return null;
    }
    if (gameSlug) {
      return getHowToPlayGuide(gameSlug);
    }
    if (activeGame) {
      return getHowToPlayGuide(activeGame);
    }
    return null;
  }, [gameSlug, activeGame]);

  // Current game info for the Trocar Jogo button display
  const currentGame = useMemo(() => {
    if (!dbGames || dbGames.length === 0) return null;
    return dbGames.find((g) => g.id === activeGuide?.id || g.id === activeGame);
  }, [dbGames, activeGuide, activeGame]);

  // Sync activeGame when a game guide is viewed explicitly via URL param
  useEffect(() => {
    if (gameSlug && activeGame !== 'All') {
      const guide = getHowToPlayGuide(gameSlug);
      if (guide && setActiveGame && activeGame !== guide.id) {
        setActiveGame(guide.id);
      }
    }
  }, [gameSlug]);

  // Switch game handler (resets to 'All' and navigates to base /como-jogar)
  const handleSwitchGame = () => {
    if (setActiveGame) {
      setActiveGame('All');
    }
    navigate('/como-jogar');
  };

  // Selected turn step state (starts at 0)
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);

  // Reset selected step to 0 when game changes
  useEffect(() => {
    setSelectedStepIndex(0);
  }, [activeGuide?.slug]);

  // If activeGame is 'All' or no guide is resolved, render GameSelector following the standard page layout
  if (activeGame === 'All' || !activeGuide) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          selectedGameId={activeGame}
          onSelectGame={(id, game) => {
            const num = Number(id);
            if (setActiveGame) {
              setActiveGame(num);
            }
            const slug = game?.slug || getGameSlug(num) || String(id);
            navigate(`/como-jogar/${slug}`);
          }}
          title="Escolha um Jogo"
          subtitle="Para aprender os objetivos de vitória, preparação de mesa e regras oficiais, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  // Active Guide View (Matching user's screenshot layout)
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-10 text-slate-100 font-sans">
      {/* Top Bar: Back link + Quick Game Switcher */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={handleSwitchGame}
          className="text-sky-400 hover:text-sky-300 font-semibold text-sm flex items-center gap-1.5 transition-colors group cursor-pointer"
        >
          <i className="fas fa-arrow-left text-xs group-hover:-translate-x-0.5 transition-transform"></i>
          <span>Todos os guias</span>
        </button>
      </div>

      {/* Main Title & Lead */}
      <div className="space-y-4">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
          {activeGuide.title}
        </h1>
        <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-normal">
          {activeGuide.lead}
        </p>
      </div>

      <div className="h-px bg-slate-800/80 w-full"></div>

      {/* Guia Rápido */}
      <section className="space-y-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
          Guia rápido de {activeGuide.gameName}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 max-w-2xl">
          <div className="bg-[#0f172a]/90 border border-sky-900/40 rounded-xl p-4 shadow-sm relative overflow-hidden backdrop-blur-sm">
            <p className="text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              JOGADORES
            </p>
            <p className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {activeGuide.quickGuide.players}
            </p>
          </div>

          <div className="bg-[#0f172a]/90 border border-sky-900/40 rounded-xl p-4 shadow-sm relative overflow-hidden backdrop-blur-sm">
            <p className="text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              MÃO INICIAL
            </p>
            <p className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {activeGuide.quickGuide.handSize}
            </p>
          </div>

          <div className="bg-[#0f172a]/90 border border-sky-900/40 rounded-xl p-4 shadow-sm relative overflow-hidden backdrop-blur-sm">
            <p className="text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              VITÓRIA
            </p>
            <p className="text-base sm:text-lg font-bold text-white tracking-tight leading-snug">
              {activeGuide.quickGuide.victory}
            </p>
          </div>
        </div>
      </section>

      {/* O turno, passo a passo */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-3">
        <h3 className="text-xl font-bold text-white tracking-tight">
          O turno, passo a passo
        </h3>

        <div className="flex flex-wrap items-center gap-2.5 my-3">
          {activeGuide.turnSteps.map((step, idx) => {
            const isSelected = selectedStepIndex === idx;
            return (
              <button
                type="button"
                key={step.step}
                onClick={() => setSelectedStepIndex(idx)}
                className={`text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm cursor-pointer transition-all active:scale-95 text-left ${
                  isSelected
                    ? 'bg-sky-500 text-slate-950 font-bold shadow-md shadow-sky-500/20 ring-2 ring-sky-400/50'
                    : step.isDashed
                    ? 'bg-[#101e33] border border-dashed border-sky-500/60 text-sky-200 hover:border-sky-400 hover:text-white'
                    : 'bg-[#132238] border border-sky-700/60 text-white hover:border-sky-500 hover:bg-[#162742]'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full font-bold text-xs flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-slate-950 text-sky-400' : 'bg-sky-500 text-slate-950'
                  }`}
                >
                  {step.step}
                </span>
                <span>{step.name}</span>
              </button>
            );
          })}
        </div>

        {/* Subtexto dinâmico de acordo com a fase selecionada */}
        <p className="text-xs sm:text-sm text-slate-400 italic leading-relaxed transition-all min-h-[1.25rem]">
          {activeGuide.turnSteps[selectedStepIndex]?.desc || activeGuide.turnSubtext}
        </p>
      </section>

      {/* Objetivo */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-2">
        <h3 className="text-xl font-bold text-white tracking-tight">
          Objetivo
        </h3>
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
          {activeGuide.objective}
        </p>
      </section>

      {/* Preparação */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-2">
        <h3 className="text-xl font-bold text-white tracking-tight">
          Preparação
        </h3>
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
          {activeGuide.preparation}
        </p>
      </section>

      {/* No seu turno */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-2">
        <h3 className="text-xl font-bold text-white tracking-tight">
          No seu turno
        </h3>
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
          {activeGuide.onYourTurn}
        </p>
      </section>

      {/* Como ganhar */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-2">
        <h3 className="text-xl font-bold text-white tracking-tight">
          Como ganhar
        </h3>
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
          {activeGuide.howToWin}
        </p>
      </section>

      {/* Glossário */}
      <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-4">
        <h3 className="text-xl font-bold text-white tracking-tight">
          Glossário
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 my-2">
          {activeGuide.glossary.map((item, index) => (
            <div key={index} className="space-y-1">
              <h4 className="text-sky-400 font-bold text-base">
                {item.term}
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                {item.definition}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Formatos */}
      {activeGuide.formats && activeGuide.formats.length > 0 && (
        <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-3">
          <h3 className="text-xl font-bold text-white tracking-tight">
            Formatos de {activeGuide.gameName}
          </h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {activeGuide.formats.map((fmt, i) => (
              <span
                key={i}
                className="px-4 py-1.5 rounded-full text-xs font-semibold bg-[#111e33] border border-sky-800/40 text-sky-300 hover:border-sky-500/60 transition-colors"
              >
                {fmt}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Tipos de carta */}
      {activeGuide.cardTypes && activeGuide.cardTypes.length > 0 && (
        <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-3">
          <h3 className="text-xl font-bold text-white tracking-tight">
            Tipos de carta
          </h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {activeGuide.cardTypes.map((type, i) => (
              <span
                key={i}
                className="px-3 py-1 rounded-full text-xs font-medium bg-[#0f1b2d] border border-sky-900/40 text-slate-300 hover:text-white transition-colors"
              >
                {type}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Referências Externas & Material Oficial */}
      {activeGuide.references && (activeGuide.references.official_rules_url || activeGuide.references.tutorial_url) && (
        <section className="border-l-4 border-sky-400 pl-4 py-0.5 space-y-4">
          <div>
            <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
              <i className="fas fa-arrow-up-right-from-square text-sky-400 text-sm"></i>
              <span>Referências Externas & Materiais Oficiais</span>
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Consulte as fontes oficiais, livros de regras e tutoriais autorizados para {activeGuide.gameName}:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
            {activeGuide.references.official_rules_url && (
              <a
                href={activeGuide.references.official_rules_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-4 bg-[#0f172a]/90 hover:bg-[#132238] border border-sky-900/40 hover:border-sky-500/60 rounded-2xl flex items-center justify-between transition-all shadow-sm"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <i className="fas fa-book text-base"></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-black uppercase tracking-wider text-sky-400 block mb-0.5">
                      Manual de Regras
                    </span>
                    <p className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors truncate">
                      Regras Oficiais ({activeGuide.gameName})
                    </p>
                  </div>
                </div>
                <i className="fas fa-external-link-alt text-xs text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all shrink-0 ml-2"></i>
              </a>
            )}

            {activeGuide.references.tutorial_url && (
              <a
                href={activeGuide.references.tutorial_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-4 bg-[#0f172a]/90 hover:bg-[#132238] border border-red-900/30 hover:border-red-500/60 rounded-2xl flex items-center justify-between transition-all shadow-sm"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <i className="fab fa-youtube text-lg"></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-black uppercase tracking-wider text-red-400 block mb-0.5">
                      Vídeo Tutorial
                    </span>
                    <p className="text-sm font-bold text-white group-hover:text-red-300 transition-colors truncate">
                      {activeGuide.references.tutorial_title || `Tutorial de ${activeGuide.gameName}`}
                    </p>
                  </div>
                </div>
                <i className="fas fa-external-link-alt text-xs text-slate-500 group-hover:text-red-400 group-hover:translate-x-0.5 transition-all shrink-0 ml-2"></i>
              </a>
            )}
          </div>
        </section>
      )}

      {/* Quick Action Footer */}
      <div className="pt-8 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={handleSwitchGame}
          className="text-xs font-bold text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Voltar para todos os guias</span>
        </button>
      </div>
    </div>
  );
};
